import { Link } from 'react-router-dom';
import { useSettings } from '@/lib/useSettings';
import { ETERNITY } from '@/content/eternity';
import WishToy from '@/components/WishToy';

/**
 * Site footer — shared by all public pages. Bold wordmark, nav, visible
 * socials with Patreon context, contact email, copyright.
 */

const Footer = () => {
    const settings = useSettings();

    return (
        <footer className="mx-footer">
            <div className="mx-footer-top">
                <p className="mx-footer-mark">MAXIMUM</p>
                <div className="mx-footer-cols">
                    <div className="mx-footer-col">
                        <p className="mx-footer-label">SITE</p>
                        <Link to="/" className="mx-footer-link">Home</Link>
                        <Link to="/eternity" className="mx-footer-link mx-footer-link-accent">ETERNITY</Link>
                        <Link to="/studio" className="mx-footer-link">Studio</Link>
                    </div>
                    <div className="mx-footer-col">
                        <p className="mx-footer-label">CHANNELS</p>
                        <a href={settings.socialYoutube} target="_blank" rel="noopener noreferrer" className="mx-footer-link">YouTube</a>
                        <a href={settings.socialInstagram} target="_blank" rel="noopener noreferrer" className="mx-footer-link">Instagram</a>
                        <a href={settings.socialTiktok} target="_blank" rel="noopener noreferrer" className="mx-footer-link">TikTok</a>
                        {settings.socialTwitter && (
                            <a href={settings.socialTwitter} target="_blank" rel="noopener noreferrer" className="mx-footer-link">X</a>
                        )}
                    </div>
                    <div className="mx-footer-col">
                        <p className="mx-footer-label">SUPPORT</p>
                        <a href={settings.socialPatreon} target="_blank" rel="noopener noreferrer" className="mx-footer-link">Patreon</a>
                        <p className="mx-footer-context">{ETERNITY.patreonContext}</p>
                    </div>
                    <div className="mx-footer-col">
                        <p className="mx-footer-label">CONTACT</p>
                        <a href={`mailto:${settings.contactEmail}`} className="mx-footer-link">{settings.contactEmail}</a>
                    </div>
                </div>
            </div>
            <div className="mx-footer-bottom">
                <p>
                    © {new Date().getFullYear()} Maximum Productions. All rights reserved.
                    <Link to="/remember" className="mx-footer-rabbit" aria-label="follow">🐇</Link>
                </p>
                <span className="mx-footer-wish"><WishToy /></span>
                <p className="mx-footer-film">ETERNITY — {ETERNITY.statusLine}</p>
            </div>

            <style>{`
                .mx-footer {
                    background: #000;
                    border-top: 1px solid rgba(255, 255, 255, 0.1);
                    padding: clamp(2.5rem, 6vw, 4.5rem) clamp(1rem, 4vw, 3rem) 1.6rem;
                }
                .mx-footer-top {
                    display: flex;
                    justify-content: space-between;
                    gap: 2.5rem;
                    flex-wrap: wrap;
                    margin-bottom: clamp(2rem, 5vw, 3.5rem);
                }
                .mx-footer-mark {
                    font-family: var(--font-display);
                    font-weight: 700;
                    font-size: clamp(2.4rem, 6vw, 4.4rem);
                    line-height: 0.9;
                    color: #fff;
                    margin: 0;
                    letter-spacing: 0.01em;
                }
                .mx-footer-cols {
                    display: flex;
                    gap: clamp(1.8rem, 4.5vw, 4.5rem);
                    flex-wrap: wrap;
                }
                .mx-footer-col {
                    display: flex;
                    flex-direction: column;
                    gap: 0.55rem;
                    min-width: 110px;
                    max-width: 220px;
                }
                .mx-footer-label {
                    font-family: var(--font-mono);
                    font-size: 0.62rem;
                    letter-spacing: 0.22em;
                    color: var(--color-text-muted);
                    margin: 0 0 0.3rem;
                }
                .mx-footer-link {
                    font-size: 0.85rem;
                    font-weight: 500;
                    color: var(--color-text-secondary);
                }
                .mx-footer-link:hover { color: #fff; }
                .mx-footer-link-accent { color: var(--color-accent-secondary); font-weight: 700; }
                .mx-footer-link-accent:hover { color: #fff; }
                .mx-footer-context {
                    font-size: 0.72rem;
                    color: var(--color-text-muted);
                    margin: 0;
                    line-height: 1.45;
                }
                .mx-footer-bottom {
                    display: grid;
                    grid-template-columns: 1fr auto 1fr;
                    align-items: center;
                    gap: 1rem;
                    border-top: 1px solid rgba(255, 255, 255, 0.08);
                    padding-top: 1.3rem;
                }
                .mx-footer-bottom p {
                    font-size: 0.72rem;
                    color: var(--color-text-muted);
                    margin: 0;
                }
                .mx-footer-wish { justify-self: center; display: inline-flex; }
                .mx-footer-film {
                    justify-self: end;
                    text-align: right;
                    font-family: var(--font-mono);
                    letter-spacing: 0.08em;
                }
                /* the white rabbit — discreet until you look */
                .mx-footer-rabbit {
                    margin-left: 0.6em;
                    font-size: 0.85em;
                    opacity: 0.12;
                    transition: opacity var(--transition-base);
                    text-decoration: none;
                }
                .mx-footer-rabbit:hover { opacity: 1; }
                @media (max-width: 600px) {
                    .mx-footer-bottom {
                        grid-template-columns: 1fr;
                        justify-items: center;
                        text-align: center;
                        gap: 0.85rem;
                    }
                    .mx-footer-film { justify-self: center; text-align: center; }
                }
            `}</style>
        </footer>
    );
};

export default Footer;
